/**
 * リクエストウィンドウ — 2026-07-12
 *
 * エージェント（LINE WORKS bot / Web版）からの実行リクエストを、ブラウザ左上に通知として表示する。
 * 例: 「1234、出発」→ 車両1234にアサインされ本日貸出となる予約の候補を表示 →
 *     候補を選んで「実行」→ daylist の貸出DnDと同じ効果（予約→利用中・チェック初期化・車両→出発中）。
 *     返却は 予約→貸出済み・車両→送り中。
 *
 * - 全ブラウザに Pusher（private-dispatch-updates / agent.request.*）で配信。
 *   誰かが「実行」or「閉じる」するとアトミックに解決され、全画面から消える（二重実行防止）
 *   取りこぼしの保険に 60 秒ごとと、タブに戻ったときにも一覧を取り直す
 * - 「閉じる」= オペレータが手動で処理する（リクエストは対応済み扱い）
 * - 実行はワンクリック一括（オペレータの「実行」クリック自体が確認）
 *
 * 仕様書: md/master_md/feature-request-window.md
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Paper, Typography, Button, Chip, CircularProgress, Badge, Fab, IconButton } from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { pusherService } from '../services/pusherService';
import { reservationSyncService } from '../services/reservationSyncService';
import { fetchDispatchReservations, saveDispatchReservation } from '../api/dispatchService';
import { updateVehicle } from '../api/vehicleService';
import { LINE_SYNC_KEY } from '../config/instances';
import { getStoreKey } from '../config/backend';
import { logEvent } from '../lib/eventLog';
import { getArrivalContact, isAlreadyPlacedForArrival } from '../lib/arrivalContact';
import { dropStaleAltShuttleKey } from '../lib/transportVehicle';

const REQUESTS_API_URL = (process.env.REACT_APP_REQUESTS_API_URL as string)
    || 'https://booking.trailernchu.co.jp/sys/requests/api.php';
const API_KEY = LINE_SYNC_KEY;

// ステータスID（backend 実データで確認済み・useReservationStatusChange と同じ値）
const RESERVATION_IN_USE = 3;   // 利用中
const RESERVATION_DONE = 2;     // 貸出済み
const VEHICLE_DEPARTING = 1;    // 出発中
const VEHICLE_SENDING = 10;     // 送り中
// 車両位置ステータス（VehiclePositionMap の ZONE_LOCATION_STATUS と同じ採番）
const LOCATION_TO_SHOP = 2;     // →店舗（to_naha_shop）。返却連絡の「実行」で設定

interface AgentRequest {
    id: number;
    type: 'departure' | 'return' | 'line_arrival';
    vehicle_id: number | null;
    vehicle_label: string;
    note: string;
    source: string;
    created_by: string;
    created_at: string;
    // line_arrival 用（2026-07-12）
    reservation_id?: string | null;
    pickup_zone?: string | null;   // '未'|'ロビー'|'14番'|'送迎車'|'直接来店'
    customer_name?: string | null;
    line_user_id?: string | null;
    // line_arrival の貸出/返却区別（2026-07-25。無し/null=旧データ=貸出(pickup) 扱い）
    arrival_kind?: 'pickup' | 'return' | null;
}

// pickup_zone の値 → 画面表示ラベル（VehiclePositionMap の PICKUP_SUB_ZONES と対応）
const PICKUP_ZONE_LABELS: Record<string, string> = {
    '未': '連絡あり', 'ロビー': 'ロビー', '14番': '14番', '送迎車': '送迎車',
    '返却車': '返却車', '乗降場': '乗降場', '直接来店': '直接来店',
};

interface Candidate {
    id: string;
    contact_code: string;
    customer_name: string;
    start_date: string;
    end_date: string;
    status: number;
}

const callApi = async (action: string, body: any): Promise<any> => {
    const res = await fetch(`${REQUESTS_API_URL}?action=${action}&api_key=${API_KEY}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    return res.json();
};

const fmtTime = (iso: string) => {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '' : `${d.getMonth() + 1}/${d.getDate()} ${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** リクエスト1件分のカード */
const RequestCard: React.FC<{ req: AgentRequest; onResolved: (id: number) => void }> = ({ req, onResolved }) => {
    const [candidates, setCandidates] = useState<Candidate[] | null>(null);
    const [selected, setSelected] = useState<Candidate | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const isDeparture = req.type === 'departure';

    // 候補予約: 出発=本日開始・予約確定(1) / 返却=利用中(3)
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const today = new Date();
                const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                const from = new Date(today); from.setDate(from.getDate() - (isDeparture ? 0 : 30));
                const to = new Date(today); to.setDate(to.getDate() + 1);
                const list: any[] = await fetchDispatchReservations({ startDate: ymd(from), endDate: ymd(to) });
                const cands = (list || [])
                    .filter((r: any) => req.vehicle_id !== null && Number(r.vehicle_id) === Number(req.vehicle_id))
                    .filter((r: any) => {
                        const st = Number(r.status_code ?? r.status);
                        if (isDeparture) {
                            return st === 1 && String(r.start_date || '').slice(0, 10) === ymd(today);
                        }
                        return st === RESERVATION_IN_USE;
                    })
                    .map((r: any) => ({
                        id: String(r.id), contact_code: r.contact_code || '', customer_name: r.customer_name || '',
                        start_date: r.start_date || '', end_date: r.end_date || '', status: Number(r.status_code ?? r.status),
                    }));
                if (!alive) return;
                setCandidates(cands);
                if (cands.length === 1) setSelected(cands[0]); // 大体1つ → 自動選択
            } catch (e) {
                if (alive) { setCandidates([]); setError('候補の取得に失敗しました'); }
            }
        })();
        return () => { alive = false; };
    }, [req.id, req.vehicle_id, isDeparture]);

    // ワンクリック一括実行（daylist の貸出/返却DnDと同じ効果）
    const execute = async () => {
        if (!selected || busy) return;
        setBusy(true);
        setError('');
        try {
            // 1) 予約を fresh 取得し {...fresh, status} で保存（ref-save-reservation.md のパターン）
            const fresh: any[] = await fetchDispatchReservations({ id: Number(selected.id) } as any);
            const target = (fresh || []).find((r: any) => String(r.id) === String(selected.id));
            if (!target) throw new Error('予約の最新データを取得できませんでした');
            await saveDispatchReservation({ ...target, status: isDeparture ? RESERVATION_IN_USE : RESERVATION_DONE });

            // 2) 車両側: 出発=チェック初期化+出発中 / 返却=送り中
            if (req.vehicle_id !== null) {
                if (isDeparture) {
                    await updateVehicle({
                        id: req.vehicle_id,
                        status: VEHICLE_DEPARTING,
                        check_situations: [],
                        cleaning: false, car_wash: false, check1: false, check2: false,
                        tire_groove: false, tire_pressure: false,
                    });
                } else {
                    await updateVehicle({ id: req.vehicle_id, status: VEHICLE_SENDING });
                }
            }

            // 3) リクエストを解決（全画面から消える）
            const username = (() => { try { return JSON.parse(localStorage.getItem('userData') || '{}').username || ''; } catch { return ''; } })();
            await callApi('resolve', { id: req.id, result: 'executed', by: username });
            onResolved(req.id);
        } catch (e: any) {
            setError(`実行に失敗しました: ${e?.message || e}`);
        } finally {
            setBusy(false);
        }
    };

    const close = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const username = (() => { try { return JSON.parse(localStorage.getItem('userData') || '{}').username || ''; } catch { return ''; } })();
            await callApi('resolve', { id: req.id, result: 'closed', by: username });
            onResolved(req.id);
        } catch (e) {
            logEvent({ action: 'request_window.close', target: String(req.id), result: 'fail', err: e });
            setError('閉じられませんでした。もう一度お試しください');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Paper elevation={6} sx={{ width: 340, p: 1.5, borderRadius: 2, border: '1px solid', borderColor: isDeparture ? '#f59e0b' : '#3b82f6' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                <NotificationsActiveIcon sx={{ fontSize: 18, color: isDeparture ? '#f59e0b' : '#3b82f6' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 800, flexGrow: 1 }}>
                    {req.vehicle_label} {isDeparture ? '出発' : '返却'}リクエスト
                </Typography>
                <Chip size="small" label={req.source === 'lineworks' ? 'LINE WORKS' : 'エージェント'} sx={{ height: 18, fontSize: 10 }} />
            </Box>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1 }}>
                {req.created_by && `${req.created_by} ・ `}{fmtTime(req.created_at)}{req.note && ` ・ ${req.note}`}
            </Typography>

            {/* 候補予約 */}
            {candidates === null ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><CircularProgress size={18} /></Box>
            ) : candidates.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#ef4444', mb: 1 }}>
                    {req.vehicle_id === null ? '車両を特定できませんでした。' : `該当する${isDeparture ? '本日貸出' : '利用中'}の予約が見つかりません。`}
                    手動でご確認ください。
                </Typography>
            ) : (
                <Box sx={{ mb: 1 }}>
                    {candidates.map((c) => (
                        <Paper
                            key={c.id}
                            variant="outlined"
                            onClick={() => setSelected(c)}
                            sx={{
                                p: 1, mb: 0.5, cursor: 'pointer', borderRadius: 1.5,
                                borderColor: selected?.id === c.id ? '#0ea5e9' : '#e2e8f0',
                                bgcolor: selected?.id === c.id ? '#f0f9ff' : '#fff',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <DirectionsCarIcon sx={{ fontSize: 14, color: '#64748b' }} />
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{c.customer_name || '（名前なし）'}</Typography>
                                <Typography variant="caption" sx={{ color: '#64748b' }}>{c.contact_code}</Typography>
                            </Box>
                            <Typography variant="caption" sx={{ color: '#475569' }}>
                                {fmtTime(c.start_date)} 〜 {fmtTime(c.end_date)}
                            </Typography>
                        </Paper>
                    ))}
                </Box>
            )}

            {error && <Typography variant="caption" sx={{ color: '#ef4444', display: 'block', mb: 0.5 }}>{error}</Typography>}

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button size="small" onClick={close} disabled={busy}>閉じる</Button>
                <Button size="small" variant="contained" onClick={execute} disabled={busy || !selected}
                    color={isDeparture ? 'warning' : 'primary'}>
                    {busy ? <CircularProgress size={16} /> : '実行'}
                </Button>
            </Box>
        </Paper>
    );
};

/** 到着/返却メッセージ通知カード（type='line_arrival'）
 *  「実行」の効果は arrival_kind で分岐:
 *   - 貸出（pickup・既定）: 紐付け予約の json_data.data['空港'] をパース済みゾーンに更新
 *     （daylist 車両ポジションのピックアップサブゾーンDnDと同じ効果）
 *   - 返却（return）: 予約に紐づく車両の位置ステータスを「→店舗」に変更（空港配置はしない） */
const LineArrivalCard: React.FC<{ req: AgentRequest; onResolved: (id: number) => void }> = ({ req, onResolved }) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    // 返却連絡（arrival_kind='return'）は空港配置ではなく車両位置→店舗の操作になる
    const isReturn = req.arrival_kind === 'return';

    // 予約と紐づいている場合は **予約者名** を出す（2026-08-25）。
    // req.customer_name は LINE の表示名（例: Kozüe . Arai）なので、そのままだと
    // 誰の到着連絡か現場で判別できない。紐づいていない時だけ LINE 名にフォールバックする。
    // 紐づく予約そのものも持つ（2026-11-01）。名前のほか「すでに同じゾーンに配置済みか」を見るため。
    // 車両ポジションで動かされたら同期キャッシュの更新で追いかける
    const [linkedRes, setLinkedRes] = useState<any | null>(() => (
        req.reservation_id ? (reservationSyncService.getReservation(req.reservation_id) || null) : null
    ));
    const fromCache = useRef(!!linkedRes);
    useEffect(() => {
        if (!req.reservation_id) return;
        return reservationSyncService.subscribe(() => {
            const hit = reservationSyncService.getReservation(req.reservation_id as string);
            if (hit) { fromCache.current = true; setLinkedRes(hit); }
        });
    }, [req.reservation_id]);
    useEffect(() => {
        if (!req.reservation_id || fromCache.current) return;
        // 同期キャッシュに無い（範囲外・キャッシュ未読込）ときだけ取りに行く
        let alive = true;
        (async () => {
            try {
                const list: any[] = await fetchDispatchReservations({ id: Number(req.reservation_id) } as any);
                const hit = (list || []).find((r: any) => String(r.id) === String(req.reservation_id));
                if (alive && hit && !fromCache.current) setLinkedRes(hit);
            } catch (e) {
                // 取れなければ LINE 名のまま・配置済みの判定もしない（従来どおり実行を出す）
                logEvent({ action: 'request_window.fetch_linked', target: String(req.reservation_id), result: 'fail', err: e });
            }
        })();
        return () => { alive = false; };
    }, [req.reservation_id]);
    const linkedName: string | null = linkedRes?.customer_name || null;

    const lineName = req.customer_name || '';
    const displayName = linkedName || lineName || '（名前なし）';

    const zone = req.pickup_zone || '未';
    const zoneLabel = PICKUP_ZONE_LABELS[zone] || zone;
    // すでに同じ所に配置済み（2026-11-01）: 実行しても置き直すだけなので「実行」は出さず、配置済みと表示する。
    // 連絡の文面は読んでもらうため、カード自体は出したままにする
    const placed = !isReturn && !!linkedRes ? getArrivalContact(linkedRes) : null;
    const alreadyPlaced = !!placed && isAlreadyPlacedForArrival(placed, zone);
    const placedLabel = placed ? (PICKUP_ZONE_LABELS[placed.airport] || placed.airport) : '';
    const canExecute = !!req.reservation_id && !alreadyPlaced; // 予約を特定できない・配置済みのカードは「閉じる」のみ

    const getUsername = () => { try { return JSON.parse(localStorage.getItem('userData') || '{}').username || ''; } catch { return ''; } };

    const execute = async () => {
        if (!canExecute || busy) return;
        setBusy(true);
        setError('');
        try {
            // fresh 取得 → {...fresh, <差分>} パターン（ref-save-reservation.md）
            const fresh: any[] = await fetchDispatchReservations({ id: Number(req.reservation_id) } as any);
            const target = (fresh || []).find((r: any) => String(r.id) === String(req.reservation_id));
            if (!target) throw new Error('予約の最新データを取得できませんでした');
            if (isReturn) {
                // 返却連絡: 車両の位置ステータスを →店舗 に（予約・車両ステータス自体は変えない。
                // 返却完了処理は従来の返却フロー（DnD / returnリクエスト）のまま）
                const vehicleId = target.vehicle_id ?? target.detail?.vehicle_id ?? null;
                if (!vehicleId) throw new Error('予約に車両が割り当てられていません（手動でご確認ください）');
                await updateVehicle({ id: Number(vehicleId), location_status: LOCATION_TO_SHOP });
            } else {
                const jd = target.json_data || target.detail?.json_data || { data: {} };
                // 到着連絡='有り' も併せて設定する（2026-07-26 修正）。
                // 空港サブゾーンだけ設定しても、ピックアップエリア（連絡あり等）への配置条件は
                // 到着連絡='有り' なので、従来は実行しても予約リストに残ったままだった。
                // daylist 車両ポジションのピックアップDnD と同一の書き込み内容にする。
                const newData = { ...(jd.data || {}), '到着連絡': '有り', '空港': zone };
                dropStaleAltShuttleKey(newData); // 空港を送迎車以外にしたら別車両番号も消す
                const newJsonData = { ...jd, data: newData };
                const saved = await saveDispatchReservation({
                    ...target,
                    json_data: newJsonData,
                    detail: { ...(target.detail || {}), json_data: newJsonData },
                });
                // 同期キャッシュへ即時反映（車両ポジションの予約リスト→連絡ありへ即座に移動）
                if (saved) reservationSyncService.applyLocalUpdate(saved);
            }
            await callApi('resolve', { id: req.id, result: 'executed', by: getUsername() });
            onResolved(req.id);
        } catch (e: any) {
            setError(`実行に失敗しました: ${e?.message || e}`);
        } finally {
            setBusy(false);
        }
    };

    const close = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await callApi('resolve', { id: req.id, result: 'closed', by: getUsername() });
            onResolved(req.id);
        } catch (e) {
            logEvent({ action: 'request_window.close', target: String(req.id), result: 'fail', err: e });
            setError('閉じられませんでした。もう一度お試しください');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Paper elevation={6} sx={{ width: 340, p: 1.5, borderRadius: 2, border: isReturn ? '1px solid #3949ab' : '1px solid #10b981' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                <NotificationsActiveIcon sx={{ fontSize: 18, color: isReturn ? '#3949ab' : '#10b981' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 800, flexGrow: 1 }}>
                    {isReturn ? '返却連絡あり' : 'メッセージあり'}
                </Typography>
                {isReturn && (
                    <Chip size="small" label="返却" sx={{ height: 18, fontSize: 10, bgcolor: '#e8eaf6', color: '#3949ab' }} />
                )}
                <Chip
                    size="small"
                    label={req.source === 'checkin' ? 'チェックイン' : 'LINE'}
                    sx={req.source === 'checkin'
                        ? { height: 18, fontSize: 10, bgcolor: '#dbeafe', color: '#1d4ed8' }
                        : { height: 18, fontSize: 10, bgcolor: '#dcfce7', color: '#15803d' }}
                />
            </Box>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>
                {fmtTime(req.created_at)}
            </Typography>
            <Typography
                variant="body2"
                sx={{ fontWeight: 700, mb: 0.5 }}
                // 置き換えた場合は LINE 表示名を title で補う（チャット画面と突き合わせる用）
                title={linkedName && lineName && linkedName !== lineName ? `LINE表示名: ${lineName}` : undefined}
            >
                {displayName} 様{req.vehicle_label && `（${req.vehicle_label}）`}
            </Typography>
            {req.note && (
                <Paper variant="outlined" sx={{ p: 1, mb: 0.75, borderRadius: 1.5, bgcolor: '#f8fafc' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{req.note}</Typography>
                </Paper>
            )}
            {alreadyPlaced ? (
                <Typography variant="body2" sx={{ color: '#475569', mb: 1 }} data-testid="arrival-already-placed">
                    すでに <b>{placedLabel}</b> に配置済みです（実行は不要です）
                </Typography>
            ) : canExecute ? (
                isReturn ? (
                    <Typography variant="body2" sx={{ color: '#3949ab', mb: 1 }}>
                        実行 → 車両位置を <b>→店舗</b> にします
                    </Typography>
                ) : (
                    <Typography variant="body2" sx={{ color: '#0f766e', mb: 1 }}>
                        実行 → <b>{zoneLabel}</b> に配置します
                    </Typography>
                )
            ) : (
                <Typography variant="body2" sx={{ color: '#ef4444', mb: 1 }}>
                    紐付け予約を特定できませんでした。手動でご確認ください。
                </Typography>
            )}

            {error && <Typography variant="caption" sx={{ color: '#ef4444', display: 'block', mb: 0.5 }}>{error}</Typography>}

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button size="small" onClick={close} disabled={busy}>閉じる</Button>
                {canExecute && (
                    <Button size="small" variant="contained" color="success" onClick={execute} disabled={busy}>
                        {busy ? <CircularProgress size={16} /> : '実行'}
                    </Button>
                )}
            </Box>
        </Paper>
    );
};

/** 画面左上のリクエスト通知スタック（全ページ共通マウント） */
const RequestWindow: React.FC = () => {
    const [requests, setRequests] = useState<AgentRequest[]>([]);
    // 最小化: 畳んでもリクエストは消えない（ベルバッジから呼び出せる）。新着が来たら自動で展開
    const [minimized, setMinimized] = useState(false);
    // 定期リフレッシュの新着判定用（現在表示中のリクエスト一覧の参照）
    const requestsRef = useRef<AgentRequest[]>([]);
    useEffect(() => { requestsRef.current = requests; }, [requests]);

    const refresh = useCallback(async () => {
        try {
            // 自店のリクエストだけを取得する。エンドポイントが那覇固定なのは正しい
            // （agent_requests は単一DBの共有テーブル。LINE API とは事情が異なる）
            const data = await callApi('pending', { store: getStoreKey() });
            if (data.success) {
                const next: AgentRequest[] = data.requests || [];
                // Pusher を取りこぼしていた新着があれば自動で展開（Pusher 経由と同じ挙動）
                const hasNew = next.some((r) => !requestsRef.current.some((p) => p.id === r.id));
                setRequests(next);
                if (hasNew) setMinimized(false);
            }
        } catch (e) {
            // 次の定期取得で回復する。ただし続くと Pusher 頼みになるので記録は残す
            logEvent({ action: 'request_window.pending', result: 'fail', err: e });
        }
    }, []);

    useEffect(() => {
        refresh();
        // Pusher: 作成/解決イベントで即時反映（他画面の操作も同期）
        const unsubscribe = pusherService.subscribe((eventName: string, data: any) => {
            if (eventName === 'agent.request.created' && data?.id) {
                setRequests((prev) => (prev.some((r) => r.id === data.id) ? prev : [...prev, data]));
                setMinimized(false); // 新着が来たら自動で展開
            } else if (eventName === 'agent.request.resolved' && data?.id) {
                setRequests((prev) => prev.filter((r) => r.id !== data.id));
            }
        });
        // 定期リフレッシュ（2026-07-26 追加）:
        //   Pusher が切断・遮断されている端末でもリクエストを取りこぼさないための保険。
        //   60秒おきに pending を再取得（解決済みの掃除も兼ねる）。
        const pollId = setInterval(refresh, 60 * 1000);
        // タブに戻ったとき・ウィンドウに戻ったときもすぐ取り直す（2026-11-01）。
        // 裏のタブはタイマーが間引かれるので、ほかの端末で解決済みのカードが戻った瞬間に残って見えないように
        const onVisible = () => { if (!document.hidden) refresh(); };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onVisible);
        return () => {
            unsubscribe();
            clearInterval(pollId);
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onVisible);
        };
    }, [refresh]);

    if (requests.length === 0) return null;

    // 最小化中: ベルバッジ（件数付き）。クリックで再展開して溜まったリクエストを呼び出せる
    if (minimized) {
        return (
            <Fab
                size="small" color="warning"
                onClick={() => setMinimized(false)}
                sx={{ position: 'fixed', top: 8, left: 8, zIndex: 2000 }}
                title={`未対応リクエスト ${requests.length}件`}
            >
                <Badge badgeContent={requests.length} color="error">
                    <NotificationsActiveIcon />
                </Badge>
            </Fab>
        );
    }

    return (
        <Box sx={{ position: 'fixed', top: 8, left: 8, zIndex: 2000, display: 'flex', flexDirection: 'column', gap: 1, maxHeight: '90vh', overflowY: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', bgcolor: 'rgba(255,255,255,0.85)', px: 0.75, borderRadius: 1 }}>
                    リクエスト {requests.length}件
                </Typography>
                <IconButton size="small" onClick={() => setMinimized(true)} title="最小化（ベルから呼び出せます）"
                    sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: '#fff' } }}>
                    <ExpandLessIcon fontSize="small" />
                </IconButton>
            </Box>
            {requests.map((req) => (
                req.type === 'line_arrival'
                    ? <LineArrivalCard key={req.id} req={req} onResolved={(id) => setRequests((prev) => prev.filter((r) => r.id !== id))} />
                    : <RequestCard key={req.id} req={req} onResolved={(id) => setRequests((prev) => prev.filter((r) => r.id !== id))} />
            ))}
        </Box>
    );
};

/**
 * 店舗ゲート: リクエストウィンドウは那覇専用（instances.ts features.requestWindow）。
 * 発生源（LINE WORKS N bot・顧客LINE到着/返却検知）と requests API が那覇固定のため、
 * 宮古(pit-booking)では pending 取得・Pusher 購読ごと行わない（那覇リクエストの混入防止）。
 * 内部コンポーネントを丸ごとマウントしないことで hooks 順序も安全に保つ。
 */
// ※ ブランチ由来の「那覇専用ゲート」(isRequestWindowEnabled) は廃止。
//    main 側で `callApi('pending', { store: getStoreKey() })` によるサーバ側の店舗フィルタに
//    移行済みで、そちらが「宮古に那覇のリクエストが混入する」問題の上位互換の解決になっている。
export default RequestWindow;
